import { useId, useState, type ChangeEvent } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import styles from "./TransactionsFilters.module.css";
import { Button, Collapse, Input, Select } from "../../../shared";
import type { TransactionFilterOption } from "../../../../queries/user-manager/useGetTransactionFilterDropdowns";
import { AppStrings } from "../../../../utils/constants/AppStrings";

export type TransactionsFiltersProps = {
  headerText: string;
  titleOptions: TransactionFilterOption[];
  currencyOptions: TransactionFilterOption[];
  isDropdownLoading?: boolean;

  // Draft state — what's selected pre-Apply.
  transactionTitles: string[];
  moneyType: string;
  searchValue: string;

  onTransactionTitlesChange: (value: string[] | undefined) => void;
  onMoneyTypeChange: (value: string | undefined) => void;
  onSearchValueChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * Fired when the user clicks the antd `allowClear` `×` button inside the
   * search input. The draft search value is already reset via
   * `onSearchValueChange` — this is the parent's hook to also drop any
   * currently applied search and refetch with the remaining filters.
   */
  onSearchClear: () => void;
  onSearchPressed: () => void;
  onApply: () => void;
  onClear: () => void;

  /** Drives the Clear button's disabled state. */
  hasAppliedOrDraftFilters: boolean;
};

const TransactionsFilters = ({
  headerText,
  titleOptions,
  currencyOptions,
  isDropdownLoading,
  transactionTitles,
  moneyType,
  searchValue,
  onTransactionTitlesChange,
  onMoneyTypeChange,
  onSearchValueChange,
  onSearchClear,
  onSearchPressed,
  onApply,
  onClear,
  hasAppliedOrDraftFilters,
}: TransactionsFiltersProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const panelId = useId();

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <p className={styles.headerText}>{headerText}</p>

        <div className={styles.headerActions}>
          <Input
            variant="search"
            placeholder={AppStrings.SEARCH_TRANSACTIONS}
            className={styles.searchInput}
            value={searchValue}
            onChange={(event) => {
              onSearchValueChange(event);
              if (event.type === "click") {
                onSearchClear();
              }
            }}
            allowClear
            data-testid="transactions-search-input"
          />

          <Button
            size="large"
            onClick={onSearchPressed}
            data-testid="transactions-search-button"
          >
            <SearchOutlined
              style={{
                fontSize: "20px",
                color: "var(--COLOR-LIGHT_TEXT_COLOR)",
              }}
            />
          </Button>

          <Button
            intent="primary"
            size="large"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            aria-controls={panelId}
            data-testid="transactions-filters-toggle"
          >
            <span className={styles.filterToggleInner}>
              <Filter size={18} />
              {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          </Button>
        </div>
      </div>

      <Collapse open={open} className={styles.collapseHost}>
        <div
          id={panelId}
          className={styles.filtersPanel}
          data-testid="transactions-filters-panel"
        >
          <Select
            allowClear
            mode="multiple"
            className={styles.compactFilter}
            options={titleOptions}
            value={transactionTitles}
            onChange={onTransactionTitlesChange}
            placeholder={AppStrings.TRANSACTION_METHOD}
            loading={isDropdownLoading}
            maxTagCount={1}
            virtual={false}
            data-testid="transactions-titles-filter"
          />

          <Select
            allowClear
            className={styles.compactFilter}
            options={currencyOptions}
            value={moneyType || undefined}
            onChange={onMoneyTypeChange}
            placeholder={AppStrings.CURRENCY}
            loading={isDropdownLoading}
            virtual={false}
            data-testid="transactions-money-type-filter"
          />

          <Button
            size="large"
            onClick={onApply}
            data-testid="transactions-apply-filters"
          >
            {AppStrings.APPLY}
          </Button>

          <Button
            variant="ghost"
            size="large"
            onClick={onClear}
            disabled={!hasAppliedOrDraftFilters}
            data-testid="transactions-clear-filters"
          >
            {AppStrings.CLEAR_FILTERS}
          </Button>
        </div>
      </Collapse>
    </div>
  );
};

export default TransactionsFilters;
